import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle, BookOpen, Calendar, Shield, Award, Users, Star, Play, ChevronDown, ChevronUp, Globe, DollarSign, TrendingUp, MessageSquare, FileText, Zap, Heart, Plus, Minus, X, Check, ChevronRight, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const stats = [
  { icon: <Users className="h-8 w-8 text-blue-400" />, value: "350+", label: "Active Tutors", bgColor: "bg-blue-500/20 hover:bg-blue-500/30" },
  { icon: <Heart className="h-8 w-8 text-purple-400" />, value: "98%", label: "Satisfaction Rate", bgColor: "bg-purple-500/20 hover:bg-purple-500/30" },
  { icon: <TrendingUp className="h-8 w-8 text-blue-400" />, value: "30%", label: "Average Growth", bgColor: "bg-blue-500/20 hover:bg-blue-500/30" },
  { icon: <Globe className="h-8 w-8 text-purple-400" />, value: "50+", label: "Countries", bgColor: "bg-purple-500/20 hover:bg-purple-500/30" }
];

const features = [
  { icon: <Calendar className="h-7 w-7 text-white" />, title: "Smart Scheduling", description: "AI-powered scheduling that learns your preferences and automatically finds the perfect time slots for your sessions.", gradient: "from-blue-500 to-blue-600" },
  { icon: <Users className="h-7 w-7 text-white" />, title: "Student Management", description: "Comprehensive student profiles with progress tracking, learning preferences, and parent communication tools.", gradient: "from-purple-500 to-purple-600" },
  { icon: <Shield className="h-7 w-7 text-white" />, title: "Secure Payments", description: "Bank-level security with automated invoicing and multiple payment options for your students.", gradient: "from-blue-500 to-blue-600" },
  { icon: <Award className="h-7 w-7 text-white" />, title: "Progress Tracking", description: "Monitor student development with detailed reports and milestone tracking that parents love.", gradient: "from-purple-500 to-purple-600" },
  { icon: <Sparkles className="h-7 w-7 text-white" />, title: "AI Lesson Plans", description: "Generate professional lesson plans in seconds with our AI-powered content creation tools.", gradient: "from-blue-500 to-blue-600" },
  { icon: <Zap className="h-7 w-7 text-white" />, title: "Instant Notifications", description: "Keep everyone in the loop with smart notifications for sessions, payments, and progress updates.", gradient: "from-purple-500 to-purple-600" }
];

const pricingPlans = [
  { title: "Starter", price: "8.99", description: "Perfect for new tutors or those with a small student base starting their teaching journey.", features: ["Up to 20 active students", "Basic scheduling tools", "Student progress tracking", "Email reminders", "Up to 15 AI lesson creations per month"], buttonText: "Start Free Trial", highlighted: false },
  { title: "Professional", price: "14.99", description: "Our most popular plan for established tutors who want to grow their business.", features: ["Up to 40 active students", "Advanced scheduling & reminders", "Custom emails reminders to students", "Resource library (5GB)", "Up to 50 AI lesson creations per month", "Student accessibility to your platform", "Advanced analytics", "Parent communication tools"], buttonText: "Start Free Trial", highlighted: true },
  { title: "Enterprise", price: "59.99", description: "For tutoring businesses, educational centers, and growing organizations.", features: ["Everything in Professional", "Multiple tutor accounts", "Advanced analytics & reporting", "Payment processing (1% fee)", "Resource library (20GB)", "API access", "Dedicated account manager", "White-label options", "24/7 phone support"], buttonText: "Contact Sales", highlighted: false }
];

const demoFeatures = [
  { icon: <Calendar className="h-6 w-6 text-white" />, title: "Smart Scheduling", description: "Automated booking system" },
  { icon: <TrendingUp className="h-6 w-6 text-white" />, title: "Progress Tracking", description: "Real-time analytics" },
  { icon: <DollarSign className="h-6 w-6 text-white" />, title: "Affordable Pricing", description: "Secure & automated" }
];

const testimonials = [
  { content: "Verve Tutor has completely changed the way I run my tutoring sessions. I used to spend hours planning lessons and trying to remember where each student left off. Now I can generate personalized lessons in minutes, track every student's progress, and send out reminders without lifting a finger.", name: "Nathan S.", title: "Private Tutor (Grades 5–12)" },
  { content: "Before Verve, I felt like I was just winging it. Now I operate like a real business. I've got automated scheduling, reminder emails, lesson plans on demand, and a place to store all my resources that students can access anytime.", name: "David M.", title: "Full-Time Teacher Assistant" },
  { content: "What I love most about Verve is how it helps me connect better with my students. The ability to take and organize notes on each one means I show up to every session prepared and personalized.", name: "Anisha K.", title: "High School English & History Tutor" }
];

const faqs = [
  { question: "How do I get started with Verve Tutor?", answer: "Getting started is simple and quick! Sign up for a free 14-day trial with no credit card required. Once you create your account, you can set up your profile, customize your availability, add your first students, and configure your preferences.", category: "Getting Started" },
  { question: "What security measures protect my data?", answer: "Security is our top priority. We use industry-standard AES-256 encryption for data at rest and TLS 1.3 for data in transit. Our infrastructure is hosted on GCP with multiple layers of security including firewalls, intrusion detection, and regular security audits.", category: "Security & Privacy" },
  { question: "How does student progress tracking work?", answer: "Our comprehensive progress tracking system allows you to set learning goals, track achievements, and monitor student development over time. You can create custom assessment rubrics, record session notes, upload assignments, and generate detailed progress reports.", category: "Student Management" },
  { question: "How does AI-generated lesson planning work?", answer: "Verve Tutor makes lesson creation fast and customizable using AI. Simply fill out a short form with details like subject, topic, and student level. You can also add extra instructions or learning goals to tailor the output to your teaching style.", category: "AI & Automation" },
  { question: "What happens if I cancel my subscription?", answer: "You can cancel your subscription at any time through your account settings. You'll continue to have full access to all features until the end of your current billing period.", category: "Account Management" },
  { question: "Do you offer support for educational institutions?", answer: "Yes! We have special institutional packages designed for schools, tutoring centers, and educational organizations. These include volume discounts, centralized admin controls, advanced reporting across multiple tutors, and dedicated account management.", category: "Institutional Support" }
];

const demoSteps = [
  { title: "Welcome to Verve Tutor", description: "Let's take a quick tour of how Verve Tutor transforms your tutoring business", icon: <Sparkles className="w-10 h-10 text-white" />, feature: "Your Command Center", detail: "Everything you need in one beautiful dashboard", gradient: "from-blue-500 to-blue-600" },
  { title: "Effortless Scheduling", description: "Book sessions, set recurring appointments, and sync with your calendar automatically", icon: <Users className="w-10 h-10 text-white" />, feature: "Smart Calendar", detail: "AI finds the perfect time slots", gradient: "from-blue-500 to-blue-600" },
  { title: "Track Every Win", description: "Monitor student progress with detailed analytics and beautiful visual reports", icon: <TrendingUp className="w-10 h-10 text-white" />, feature: "Progress Dashboard", detail: "Real-time insights at a glance", gradient: "from-purple-500 to-purple-600" },
  { title: "Grow Your Income", description: "Professional tools to attract more students and increase your earnings by 30% on average", icon: <Award className="w-10 h-10 text-white" />, feature: "Revenue Tracking", detail: "See your business grow in real-time", gradient: "from-blue-500 to-blue-600" },
  { title: "Ready to Get Started?", description: "Join 350+ successful tutors and start your free 14-day trial today—no credit card required!", icon: <Zap className="w-10 h-10 text-white" />, feature: "Start Free Trial", detail: "Your tutoring revolution begins now", gradient: "from-purple-500 to-purple-600" }
];

const TutorLandingPage = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [demoStep, setDemoStep] = useState(0);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDemoComplete = () => {
    setShowDemo(false);
    setDemoStep(0);
    window.location.href = '/signup';
  };

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 relative overflow-hidden">
      <div className="fixed inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-pink-600/10 pointer-events-none" />
      <div className="fixed inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptMCAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnpNMTggMThjMy4zMTQgMCA2IDIuNjg2IDYgNnMtMi42ODYgNi02IDYtNi0yLjY4Ni02LTYgMi42ODYtNiA2LTZ6bTAgMThjMy4zMTQgMCA2IDIuNjg2IDYgNnMtMi42ODYgNi02IDYtNi0yLjY4Ni02LTYgMi42ODYtNiA2LTZ6IiBzdHJva2U9IiMyMjI4MzAiIHN0cm9rZS13aWR0aD0iLjUiIG9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-20 pointer-events-none" />
      
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-slate-950/80 backdrop-blur-xl border-b border-slate-800' : ''}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-500/50">
                <BookOpen className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">Verve Tutor</span>
            </motion.div>
            
            <div className="hidden md:flex items-center space-x-8">
              {['features', 'pricing', 'testimonials', 'faq'].map(item => (
                <a key={item} href={`#${item}`} className="text-slate-400 hover:text-slate-50 transition-colors px-4 py-2 capitalize">{item}</a>
              ))}
            </div>

            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex items-center space-x-4">
              <a href="/login" className="hidden md:inline-flex text-slate-400 hover:text-slate-50 transition-colors px-4 py-2">Login</a>
              <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setShowDemo(true)} className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold shadow-lg shadow-blue-500/50 hover:shadow-xl transition-all">
                See Demo
              </motion.button>
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden p-2 rounded-lg hover:bg-slate-800 transition-colors">
                {isMenuOpen ? <Minus className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
              </button>
            </motion.div>
          </div>
        </div>

        <AnimatePresence>
          {isMenuOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="md:hidden bg-slate-950/95 backdrop-blur-xl border-b border-slate-800">
              <div className="px-6 py-4 space-y-4">
                {['features', 'pricing', 'testimonials', 'faq', 'login'].map(item => (
                  <a key={item} href={item === 'login' ? '/login' : `#${item}`} className="block py-2 text-slate-50 hover:text-blue-400 transition-colors capitalize">{item}</a>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <section className="relative pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-slate-900 border border-slate-800 rounded-full mb-8">
                <Zap className="w-4 h-4 text-blue-400" />
                <span className="text-sm text-slate-400">Transform your teaching business</span>
              </div>
              
              <h1 className="text-6xl lg:text-7xl font-black leading-tight mb-6">
                Tutoring,<br />
                <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">Reimagined</span>
              </h1>
              
              <p className="text-xl text-slate-400 mb-10 leading-relaxed">
                The most advanced platform for modern educators. Manage students, track progress, and grow your income—all in one beautiful interface.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setShowDemo(true)} className="group px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-bold text-lg shadow-lg transition-all flex items-center justify-center">
                  Watch Interactive Demo
                  <Play className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </motion.button>
                
                <motion.a whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} href="/signup" className="px-8 py-4 bg-slate-900 border-2 border-slate-800 hover:border-blue-500 text-slate-50 rounded-xl font-bold text-lg transition-all flex items-center justify-center">
                  Start Free Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </motion.a>
              </div>
              
              <div className="mt-12 flex items-center space-x-8">
                <div className="flex items-center space-x-2">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 border-2 border-slate-950" />
                    ))}
                  </div>
                  <span className="text-sm text-slate-400">350+ tutors</span>
                </div>
                <div className="flex items-center space-x-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                  <span className="text-sm text-slate-400 ml-2">4.9/5 rating</span>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-blue-500 to-purple-600 opacity-20 blur-3xl rounded-3xl" />
              <div className="relative p-8 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl">
                <div className="space-y-6">
                  {[
                    { icon: Users, label: 'Active Students', value: '28', change: '+12%', gradient: 'from-blue-500 to-purple-600' },
                    { icon: TrendingUp, label: 'Monthly Revenue', value: '$4,250', change: '+28%', gradient: 'from-purple-500 to-pink-600' },
                    { icon: Award, label: 'Completion Rate', value: '96%', change: 'Excellent', gradient: 'blue-500/20', isOpacity: true }
                  ].map((stat, idx) => (
                    <motion.div key={idx} initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.4 + idx * 0.1 }} className="flex items-center space-x-4 p-4 bg-slate-950 rounded-xl border border-slate-800">
                      <div className={`w-12 h-12 rounded-full ${stat.isOpacity ? 'bg-blue-500/20' : `bg-gradient-to-br ${stat.gradient}`} flex items-center justify-center`}>
                        <stat.icon className={`w-6 h-6 ${stat.isOpacity ? 'text-blue-400' : 'text-white'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm text-slate-400">{stat.label}</div>
                        <div className="text-2xl font-bold text-slate-50">{stat.value}</div>
                      </div>
                      <div className="text-green-400 text-sm font-semibold">{stat.change}</div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900 border-y border-slate-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} viewport={{ once: true }} className="text-center group">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 transition-colors ${stat.bgColor}`}>{stat.icon}</div>
                <p className="text-4xl font-extrabold text-slate-50 mb-2">{stat.value}</p>
                <p className="text-lg text-slate-400">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="py-32 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-20">
            <h2 className="text-5xl lg:text-6xl font-black mb-6">
              Everything You Need,<br />
              <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">Nothing You Don't</span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">Built for modern tutors who demand excellence</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} whileHover={{ y: -8 }} className="group relative">
                <div className="absolute -inset-1 bg-gradient-to-br from-blue-500 to-purple-600 opacity-0 group-hover:opacity-20 blur-xl rounded-2xl transition-opacity" />
                <div className="relative h-full bg-slate-900 border border-slate-800 rounded-2xl transition-all group-hover:border-blue-500 p-6">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6`}>{feature.icon}</div>
                  <h3 className="text-2xl font-bold text-slate-50 mb-4">{feature.title}</h3>
                  <p className="text-base leading-relaxed text-slate-400">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="py-32 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-20">
            <h2 className="text-5xl lg:text-6xl font-black mb-6">
              Simple,<br />
              <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">Transparent Pricing</span>
            </h2>
            <p className="text-xl text-slate-400">Choose your plan, cancel anytime</p>
            <p className="text-sm text-slate-500 mt-2">Certain features may not be immediately available and are scheduled for future release</p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} whileHover={{ y: -8 }} className={`relative ${plan.highlighted ? 'lg:-mt-8' : ''}`}>
                {plan.highlighted && (
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full text-sm font-bold shadow-lg shadow-blue-500/50">Most Popular</div>
                )}
                <div className={`h-full bg-slate-900 border rounded-2xl ${plan.highlighted ? 'border-blue-500 shadow-lg shadow-blue-500/20' : 'border-slate-800'}`}>
                  <div className="text-center pb-8 p-6">
                    <h3 className="text-2xl font-bold text-slate-50 mb-2">{plan.title}</h3>
                    <div className="flex items-baseline justify-center mb-4">
                      <span className="text-5xl font-black">${plan.price}</span>
                      <span className="text-slate-400 ml-2">/month</span>
                    </div>
                    <p className="text-slate-400">{plan.description}</p>
                  </div>
                  <div className="p-6 pt-0">
                    <ul className="space-y-4 mb-8">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start space-x-3">
                          <Check className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-slate-400">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <motion.a whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} href={plan.buttonText === "Contact Sales" ? "/contact" : "/signup"} className={`block w-full py-4 rounded-xl font-bold text-center transition-all ${plan.highlighted ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg hover:shadow-blue-500/50' : 'bg-slate-800 text-slate-50 hover:bg-slate-700'}`}>
                      {plan.buttonText}
                    </motion.a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.3 }} viewport={{ once: true }} className="mt-12 text-center">
            <div className="max-w-3xl mx-auto bg-slate-900 border border-slate-800 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-slate-50 mb-4">Risk-Free Trial</h3>
              <p className="text-slate-400 mb-4">All plans include a 14-day free trial. No credit card required to start. Cancel anytime during the trial period.</p>
              <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-slate-400">
                {['No setup fees', 'Cancel anytime', 'Data export available'].map((text, i) => (
                  <div key={i} className="flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2 text-green-400" />
                    <span>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="demo" className="py-32 px-6 relative bg-gradient-to-br from-blue-900/10 via-purple-900/10 to-pink-900/10">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <h2 className="text-5xl lg:text-6xl font-black mb-6">
              See Verve Tutor<br />
              <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">In Action</span>
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Watch how Verve Tutor transforms the way you manage your tutoring business. 
              From scheduling to progress tracking, see all the features in action.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} viewport={{ once: true }} className="relative max-w-4xl mx-auto">
            <div className="relative bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden">
              <div className="relative pb-[56.25%] h-0">
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/I5i3-gqQ4dg?rel=0&modestbranding=1&showinfo=0"
                  title="Verve Tutor Demo Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
            
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} viewport={{ once: true }} className="mt-8 text-center">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {demoFeatures.map((feature, index) => (
                  <div key={index} className="text-center p-6 bg-slate-900 border border-slate-800 rounded-2xl">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mx-auto mb-4">
                      {feature.icon}
                    </div>
                    <div className="text-sm font-semibold text-slate-50 mb-2">{feature.title}</div>
                    <div className="text-xs text-slate-400">{feature.description}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.6 }} viewport={{ once: true }} className="mt-8 text-center">
              <motion.a whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} href="/signup" className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-blue-500/50 transition-all">
                <Zap className="mr-2 h-5 w-5" />
                Start Your Free Trial Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section id="testimonials" className="py-32 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-20">
            <h2 className="text-5xl lg:text-6xl font-black mb-6">
              Loved by<br />
              <span className="bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">Tutors Everywhere</span>
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Discover how tutors around the world have transformed their teaching business with Verve Tutor.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="relative">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl shadow-xl p-8 lg:p-12 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-32 h-32 bg-blue-500/10 rounded-full -translate-x-16 -translate-y-16"></div>
              <div className="absolute bottom-0 right-0 w-40 h-40 bg-purple-500/10 rounded-full translate-x-20 translate-y-20"></div>
              
              <div className="relative">
                <div className="flex justify-center mb-8">
                  <div className="flex space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-6 w-6 text-yellow-500 fill-current" />
                    ))}
                  </div>
                </div>
                
                <AnimatePresence mode='wait'>
                  <motion.div
                    key={activeTestimonial}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.5 }}
                    className="text-center"
                  >
                    <blockquote className="text-xl lg:text-2xl text-slate-300 italic leading-relaxed mb-8">
                      "{testimonials[activeTestimonial].content}"
                    </blockquote>
                    
                    <div className="flex items-center justify-center">
                      <div className="flex-shrink-0">
                        <div className="h-12 w-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-lg">
                            {testimonials[activeTestimonial].name.charAt(0)}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4 text-left">
                        <div className="text-lg font-semibold text-slate-50">
                          {testimonials[activeTestimonial].name}
                        </div>
                        <div className="text-sm text-slate-400">
                          {testimonials[activeTestimonial].title}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
              
              <div className="flex justify-center mt-8 space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTestimonial(index)}
                    className={`h-3 w-3 rounded-full transition-all duration-300 ${
                      activeTestimonial === index 
                        ? 'bg-blue-500 scale-125' 
                        : 'bg-slate-600 hover:bg-slate-500'
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="faq" className="py-32 px-6 relative">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-20">
            <h2 className="text-5xl lg:text-6xl font-black mb-6">
              Frequently<br />
              <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">Asked Questions</span>
            </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              Find detailed answers to common questions about Verve Tutor. Can't find what you're looking for? Contact our support team.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} viewport={{ once: true }} className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-6 text-left flex justify-between items-center hover:bg-slate-800 transition-colors"
                >
                  <div className="flex-1 pr-4">
                    <div className="flex items-center mb-2">
                      <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-500/20 text-blue-400 rounded-md mr-3">
                        {faq.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-50 leading-snug">
                      {faq.question}
                    </h3>
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    {openFaqIndex === index ? (
                      <Minus className="h-5 w-5 text-slate-400" />
                    ) : (
                      <Plus className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                </button>
                
                <AnimatePresence>
                  {openFaqIndex === index && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="px-6 overflow-hidden"
                    >
                      <div className="pb-6">
                        <div className="pt-2 border-t border-slate-800">
                          <p className="text-slate-400 leading-relaxed text-base">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
          
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} transition={{ delay: 0.3 }} viewport={{ once: true }} className="mt-16 text-center">
            <div className="max-w-3xl mx-auto bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-slate-800 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-slate-50 mb-4">Still have questions?</h3>
              <p className="text-slate-400 mb-6">
                Our support team is here to help you succeed. Get in touch with us through live chat, email, or phone.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} href="/contact" className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold transition-all">
                  <MessageSquare className="mr-2 h-5 w-5" />
                  Contact Support
                </motion.a>
                <motion.a whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} href="/help" className="inline-flex items-center px-6 py-3 border border-slate-600 text-slate-50 rounded-xl font-semibold hover:bg-slate-800 transition-all">
                  <FileText className="mr-2 h-5 w-5" />
                  Help Center
                </motion.a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-32 px-6 relative bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="relative">
            <div className="absolute -inset-8 bg-gradient-to-br from-blue-500 to-purple-600 opacity-20 blur-3xl rounded-3xl" />
            <div className="relative p-16 bg-slate-900 border border-slate-800 rounded-3xl">
              <h2 className="text-5xl lg:text-6xl font-black mb-6">
                Ready to <span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">Transform</span>
                <br />
                Your Teaching?
              </h2>
              <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
                Join 5,000+ tutors who've already discovered the future of education
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowDemo(true)}
                  className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-blue-500/50 transition-all flex items-center justify-center"
                >
                  See How It Works
                  <Play className="ml-2 w-5 h-5" />
                </motion.button>
                
                <motion.a
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  href="/signup"
                  className="px-8 py-4 bg-slate-800 text-slate-50 rounded-xl font-bold text-lg transition-all flex items-center justify-center"
                >
                  Start Free Trial
                  <ArrowRight className="ml-2 w-5 h-5" />
                </motion.a>
              </div>
              
              <div className="mt-8 flex items-center justify-center space-x-6 text-sm text-slate-400">
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <span>14-day free trial</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="w-4 h-4 text-green-400" />
                  <span>Cancel anytime</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <footer className="border-t border-slate-800 py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-50">Verve Tutor</span>
            </div>
            
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-6">
              <a href="/tos" className="text-sm text-slate-400 hover:text-slate-300 transition-colors">
                Terms of Service
              </a>
              <div className="text-sm text-slate-400">
                © {new Date().getFullYear()} Verve Tutor. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      </footer>

      <AnimatePresence>
        {showDemo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-950/95 backdrop-blur-xl z-50 flex items-center justify-center p-6"
            onClick={() => {
              if (demoStep === demoSteps.length - 1) {
                handleDemoComplete();
              } else {
                setShowDemo(false);
                setDemoStep(0);
              }
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-4xl w-full bg-slate-900 border border-slate-800 rounded-3xl p-8 lg:p-12 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => {
                  setShowDemo(false);
                  setDemoStep(0);
                }}
                className="absolute top-6 right-6 p-2 hover:bg-slate-800 rounded-xl transition-colors"
              >
                <X className="w-6 h-6 text-slate-400" />
              </button>

              <div className="mb-8">
                <div className="flex items-center space-x-2 mb-4">
                  {demoSteps.map((_, index) => (
                    <div
                      key={index}
                      className={`h-1 flex-1 rounded-full transition-all ${
                        index <= demoStep ? 'bg-blue-500' : 'bg-slate-700'
                      }`}
                    />
                  ))}
                </div>
                <div className="text-sm text-slate-400">
                  Step {demoStep + 1} of {demoSteps.length}
                </div>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={demoStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <h3 className="text-4xl font-black text-slate-50 mb-4">{demoSteps[demoStep].title}</h3>
                  <p className="text-xl text-slate-400 mb-8">{demoSteps[demoStep].description}</p>
                  
                  <div className="bg-slate-950 border border-slate-800 rounded-2xl p-8 mb-8 min-h-[300px] flex items-center justify-center">
                    <div className="text-center">
                      <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${demoSteps[demoStep].gradient} flex items-center justify-center mx-auto mb-6`}>
                        {demoSteps[demoStep].icon}
                      </div>
                      <div className="text-lg font-semibold text-slate-50 mb-2">{demoSteps[demoStep].feature}</div>
                      <div className="text-slate-400">{demoSteps[demoStep].detail}</div>
                    </div>
                  </div>

                  <div className="flex justify-between">
                    <button
                      onClick={() => setDemoStep(Math.max(0, demoStep - 1))}
                      disabled={demoStep === 0}
                      className="px-6 py-3 bg-slate-800 text-slate-50 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:bg-slate-700"
                    >
                      Previous
                    </button>
                    
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        if (demoStep === demoSteps.length - 1) {
                          handleDemoComplete();
                        } else {
                          setDemoStep(demoStep + 1);
                        }
                      }}
                      className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-blue-500/50 transition-all flex items-center"
                    >
                      {demoStep === demoSteps.length - 1 ? 'Start Free Trial' : 'Next'}
                      <ChevronRight className="ml-2 w-5 h-5" />
                    </motion.button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TutorLandingPage;